import React, { useState, useEffect } from 'react';
import { Plus, Trash2, X, Package, Loader2, Edit, Gift, Send, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL !== undefined ? process.env.REACT_APP_BACKEND_URL : 'http://localhost:8001';

export const TrialPacksManager = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPackId, setEditingPackId] = useState(null);
  const [formData, setFormData] = useState({ name: '', essais_count: 10, price: 9.99, description: '' });

  // Attribution state
  const [admins, setAdmins] = useState([]);
  const [assignData, setAssignData] = useState({ adminId: '', packId: '', amount: '' });
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    loadPacks();
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/grouped-by-center`);
      const centers = res.data?.centers || [];
      // Each center has an admin object; fetch their balance
      const withBalance = await Promise.all(centers.map(async (c) => {
        const a = c.admin;
        try {
          const b = await axios.get(`${API_URL}/api/trials/balance/${a.id}`);
          return { ...a, balance: b.data.essais_restants ?? 0 };
        } catch { return { ...a, balance: 0 }; }
      }));
      setAdmins(withBalance);
    } catch { /* silently ignore */ }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignData.adminId) return toast.error('Sélectionnez un centre / admin');
    const amount = assignData.packId
      ? (packs.find(p => p.id === assignData.packId)?.essais_count || 0)
      : parseInt(assignData.amount, 10);
    if (!amount || amount <= 0) return toast.error('Montant invalide');
    setAssigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/trials/assign`, null, {
        params: { admin_id: assignData.adminId, amount }
      });
      if (res.data.success) {
        toast.success(res.data.message || `${amount} essais attribués`);
        setAssignData({ adminId: '', packId: '', amount: '' });
        loadAdmins();
      } else {
        toast.error(res.data.message || 'Erreur');
      }
    } catch { toast.error('Erreur lors de l\'attribution'); }
    finally { setAssigning(false); }
  };

  const loadPacks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/trials/packs`);
      setPacks(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des packs d\'essais');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (pack) => {
    setEditingPackId(pack.id);
    setFormData({
      name: pack.name,
      essais_count: pack.essais_count,
      price: pack.price,
      description: pack.description || ''
    });
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingPackId(null);
    setFormData({ name: '', essais_count: 10, price: 9.99, description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPackId) {
        // Update existing pack
        const res = await axios.put(`${API_URL}/api/trials/packs/${editingPackId}`, {
          ...formData,
          id: editingPackId,
          created_at: packs.find(p => p.id === editingPackId)?.created_at || new Date().toISOString()
        });
        if (res.data.success) {
          toast.success('Pack mis à jour');
          handleCancel();
          loadPacks();
        }
      } else {
        // Create new pack
        const res = await axios.post(`${API_URL}/api/trials/packs`, {
          ...formData,
          id: `pack_${Math.random().toString(36).substr(2, 9)}`,
          created_at: new Date().toISOString()
        });
        if (res.data.success) {
          toast.success('Pack d\'essais créé');
          handleCancel();
          loadPacks();
        }
      }
    } catch (error) {
      toast.error(editingPackId ? 'Erreur lors de la modification' : 'Erreur lors de la création');
    }
  };

  const handleDeletePack = async (id) => {
    if (!window.confirm('Supprimer ce pack ?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/trials/packs/${id}`);
      if (res.data.success) {
        toast.success('Pack supprimé');
        loadPacks();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin h-8 w-8 text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Packs d'Essais IA</h2>
          <p className="text-muted-foreground text-sm">Créez et gérez les packs commerciaux pour les centres.</p>
        </div>
        {!showAddForm && (
          <button 
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition-all shadow-lg shadow-primary/20"
          >
            <Plus className="h-4 w-4" />
            <span>Nouveau Pack</span>
          </button>
        )}
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-8 space-y-6 animate-in slide-in-from-top-4 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-bold">{editingPackId ? 'Modifier le pack' : 'Créer un nouveau pack'}</h3>
            <button type="button" onClick={handleCancel} className="text-muted-foreground hover:text-foreground p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Nom du pack</label>
              <input 
                required
                type="text" 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Pack 50 Essais"
                className="w-full h-12 px-4 rounded-xl border bg-background/50 focus:ring-2 focus:ring-primary outline-none transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Nombre d'essais</label>
              <input 
                required
                type="number" 
                value={formData.essais_count}
                onChange={(e) => setFormData({...formData, essais_count: parseInt(e.target.value)})}
                className="w-full h-12 px-4 rounded-xl border bg-background/50 focus:ring-2 focus:ring-primary outline-none transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Prix ($)</label>
              <input 
                required
                type="number" 
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                className="w-full h-12 px-4 rounded-xl border bg-background/50 focus:ring-2 focus:ring-primary outline-none transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Description</label>
              <input 
                type="text" 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Description optionnelle"
                className="w-full h-12 px-4 rounded-xl border bg-background/50 focus:ring-2 focus:ring-primary outline-none transition-all"
              />
            </div>
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="flex-1 h-14 bg-primary text-primary-foreground rounded-2xl font-black text-lg hover:opacity-90 transition-all shadow-xl shadow-primary/20">
              {editingPackId ? 'Enregistrer les modifications' : 'Créer le pack'}
            </button>
            <button type="button" onClick={handleCancel} className="px-8 h-14 border border-border rounded-2xl font-bold hover:bg-secondary transition-all">
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* ── Attribution aux centres ───────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-3xl p-8 space-y-6 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-xl bg-emerald-500/10">
            <Gift className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h3 className="text-lg font-bold">Attribuer des essais à un centre</h3>
            <p className="text-xs text-muted-foreground">Sélectionnez un pack ou saisissez un montant manuel.</p>
          </div>
        </div>

        <form onSubmit={handleAssign} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          {/* Admin selector with balance */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Centre / Administrateur</label>
            <select
              required
              value={assignData.adminId}
              onChange={(e) => setAssignData(d => ({ ...d, adminId: e.target.value }))}
              className="w-full h-12 px-4 rounded-xl border bg-background/50 focus:ring-2 focus:ring-primary outline-none transition-all appearance-none cursor-pointer text-sm"
            >
              <option value="">Sélectionner...</option>
              {admins.map(a => (
                <option key={a.id} value={a.id}>
                  {a.nom || a.username} ({a.role === 'super_admin' ? 'Super Admin' : 'Admin'}) — {a.balance} essais
                </option>
              ))}
            </select>
          </div>

          {/* Pack selector */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Via pack <span className="normal-case font-normal opacity-60">ou montant manuel ci-dessous</span></label>
            <select
              value={assignData.packId}
              onChange={(e) => {
                const pack = packs.find(p => p.id === e.target.value);
                setAssignData(d => ({ ...d, packId: e.target.value, amount: pack ? String(pack.essais_count) : d.amount }));
              }}
              className="w-full h-12 px-4 rounded-xl border bg-background/50 focus:ring-2 focus:ring-primary outline-none transition-all appearance-none cursor-pointer text-sm"
            >
              <option value="">— Saisie manuelle —</option>
              {packs.map(p => (
                <option key={p.id} value={p.id}>{p.name} — {p.essais_count} essais</option>
              ))}
            </select>
          </div>

          {/* Amount + submit */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Nombre d'essais</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                required={!assignData.packId}
                value={assignData.amount}
                onChange={(e) => setAssignData(d => ({ ...d, amount: e.target.value, packId: '' }))}
                placeholder={assignData.packId ? String(packs.find(p => p.id === assignData.packId)?.essais_count || '') : 'Ex: 50'}
                className="flex-1 h-12 px-4 rounded-xl border bg-background/50 focus:ring-2 focus:ring-primary outline-none transition-all text-sm"
              />
              <button
                type="submit"
                disabled={assigning}
                className="h-12 px-5 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center gap-2 text-sm shadow-lg shadow-emerald-500/20"
              >
                {assigning ? <Loader2 className="animate-spin h-4 w-4" /> : <Send className="h-4 w-4" />}
                {assigning ? '' : 'Attribuer'}
              </button>
            </div>
          </div>
        </form>

        {/* Admins balance table */}
        {admins.length > 0 && (
          <div className="border border-border rounded-2xl overflow-hidden mt-2">
            <div className="flex items-center gap-2 px-5 py-3 bg-muted/30 border-b border-border">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-black uppercase tracking-widest text-muted-foreground">Soldes actuels</span>
            </div>
            <div className="divide-y divide-border">
              {admins.map(a => (
                <div key={a.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-semibold">{a.nom || a.username}</p>
                    <p className="text-xs text-muted-foreground">{a.role === 'super_admin' ? 'Super Admin' : 'Admin'} · {a.email}</p>
                  </div>
                  <span className={`text-sm font-black ${a.balance > 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                    {a.balance} essais
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Pack cards ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {packs.length === 0 ? (
          <div className="col-span-full py-20 text-center bg-card/50 rounded-3xl border-2 border-dashed border-border text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">Aucun pack d'essais configuré.</p>
            <p className="text-sm opacity-60">Commencez par créer votre premier pack commercial.</p>
          </div>
        ) : (
          packs.map(pack => (
            <div key={pack.id} className="group bg-card border border-border rounded-3xl p-6 shadow-sm hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute -top-4 -right-4 p-8 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity rotate-12">
                <Package className="h-32 w-32" />
              </div>
              
              <div className="relative z-10 space-y-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold text-foreground leading-tight">{pack.name}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-1">{pack.description || 'Pack d\'essais pour centre'}</p>
                  </div>
                  <div className="flex space-x-1">
                    <button 
                      onClick={() => handleEditClick(pack)}
                      className="p-2.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-xl transition-all"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeletePack(pack.id)}
                      className="p-2.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl transition-all"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-black text-primary uppercase tracking-widest">Contenu</span>
                    <p className="text-2xl font-black text-foreground">{pack.essais_count} <span className="text-sm font-medium opacity-60">Essais</span></p>
                  </div>
                  <div className="text-right space-y-0.5">
                    <span className="text-[10px] font-black text-green-500 uppercase tracking-widest">Tarif</span>
                    <p className="text-2xl font-black text-green-500">${pack.price}</p>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
